import * as React from 'react';

import { describeDownloadError, loadAttachment } from '../helpers/loadAttachment';

export type AttachmentPhase = 'cache' | 'downloading' | 'ready' | 'error';

export interface UseAttachmentFileResult {
    blobUrl: string | null;
    phase: AttachmentPhase;
    percent: number | null;
    loaded: number;
    total: number | null;
    error: string | null;
    retry: () => void;
}

export function useAttachmentFile(id: string | undefined, url: string, fallbackType?: string): UseAttachmentFileResult {
    const [phase, setPhase] = React.useState<AttachmentPhase>('cache');
    const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
    const [percent, setPercent] = React.useState<number | null>(null);
    const [loaded, setLoaded] = React.useState(0);
    const [total, setTotal] = React.useState<number | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [attempt, setAttempt] = React.useState(0);

    const urlRef = React.useRef<string | null>(null);
    const lastReportedPercentRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (!id) return;

        let cancelled = false;
        const controller = new AbortController();

        setPhase('cache');
        setBlobUrl(null);
        setPercent(null);
        setLoaded(0);
        setTotal(null);
        setError(null);
        lastReportedPercentRef.current = null;

        loadAttachment({
            id,
            url,
            fallbackType,
            signal: controller.signal,
            onCacheMiss: () => {
                if (cancelled) return;
                setPhase('downloading');
            },
            onProgress: (progress) => {
                if (cancelled) return;
                setLoaded(progress.loaded);
                setTotal(progress.total);

                if (progress.total && progress.total > 0) {
                    const nextPercent = Math.min(99, Math.round((progress.loaded / progress.total) * 100));
                    if (lastReportedPercentRef.current !== nextPercent) {
                        lastReportedPercentRef.current = nextPercent;
                        setPercent(nextPercent);
                    }
                } else {
                    lastReportedPercentRef.current = null;
                    setPercent(null);
                }
            },
        })
            .then((result) => {
                if (cancelled) return;

                if (urlRef.current) URL.revokeObjectURL(urlRef.current);
                const objectUrl = URL.createObjectURL(result.blob);
                urlRef.current = objectUrl;

                setPercent(100);
                setBlobUrl(objectUrl);
                setPhase('ready');
            })
            .catch((err) => {
                if (cancelled || controller.signal.aborted) return;
                setPhase('error');
                setError(describeDownloadError(err));
            });

        return () => {
            cancelled = true;
            controller.abort();
            if (urlRef.current) {
                URL.revokeObjectURL(urlRef.current);
                urlRef.current = null;
            }
        };
    }, [id, url, attempt]);

    const retry = React.useCallback(() => setAttempt((n) => n + 1), []);

    return { blobUrl, phase, percent, loaded, total, error, retry };
}
